/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { formatDate, DATE } from '../../utils/date';
import SimpleTooltip from '../simple-tooltip';
import { CompanyStatusProps, getCompanyStatus } from '../../utils/initiative';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';

interface CompanyStatusIconProps {
  company?: CompanyStatusProps;
  showLabel?: boolean;
}

const successIcon = 'fa-check-circle text-ThemeSuccessMedium';
const warningIcon = 'fa-exclamation-circle text-ThemeWarningMedium';
const dangerIcon = 'fa-exclamation-triangle text-ThemeDangerMedium';

const CompanyStatusIcon = (props: CompanyStatusIconProps) => {
  const { company, showLabel } = props;

  if (!company) {
    return <LoadingPlaceholder height={28} width={80} />;
  }

  const overallReport = getCompanyStatus(company);
  const isActive = overallReport.status === 'active';

  const onlyNotVerified =
    !overallReport.is_verified &&
    overallReport.is_premium &&
    overallReport.survey_created &&
    overallReport.survey_completed &&
    overallReport.data_shared

  let icon = warningIcon;
  let labelText = <span className='ml-2 text-ThemeWarningMedium'>Limited</span>;
  if (!overallReport.onboarded) {
    labelText = <span className='ml-2 text-ThemeDangerMedium'>Not onboarded</span>;
    icon = dangerIcon;
  } else if (isActive) {
    labelText = <span className='ml-2 text-ThemeSuccessMedium'>Active</span>;
    icon = successIcon;
  } else if (onlyNotVerified) {
    labelText = <span className='ml-2 text-ThemeWarningMedium'>Unverified</span>;
  }

  const createdMessage = overallReport.survey_created ? '' : '(report must be less than 2 years old)';
  const completedMessage = overallReport.survey_completed ? '' : '(report must be less than 2 years old)';

  const Row = ({ children, icon }: { children: any, icon: string }) => {
    return (
      <div className='mt-1 d-flex'>
        <div>
          <i className={`mr-2 fa ${icon}`} />
        </div>
        <div className='flex-fill-1'>
          {children}
        </div>
      </div>
    )
  }

  const tooltip = (
    <div className='p-3 text-left'>
      <div className='mt-2 strong'>Company status:</div>
      <Row icon={overallReport.onboarded ? successIcon : dangerIcon}>
        {overallReport.onboarded ? 'Onboarded' : 'No users onboarded'}
      </Row>
      <Row icon={overallReport.is_verified ? successIcon : warningIcon}>
        {overallReport.is_verified
          ?
          'Verified'
          :
          'Not verified by WWG (Contact customer support)'
        }
      </Row>
      <Row icon={overallReport.is_premium ? successIcon : warningIcon}>
        {overallReport.is_premium
          ?
          'Active subscription'
          :
          'Does not have an active subscription'
        }
      </Row>
      <div className='mt-2 strong'>Survey status:</div>
      <Row icon={overallReport.survey_created ? successIcon : warningIcon}>
        {company.lastSurveyDate
          ?
          <>
            Latest report:
            <div>{`${formatDate(company.lastSurveyDate, DATE.MONTH_YEAR)} ${createdMessage}`}</div>
          </>
          :
          'No reports have been Created'
        }
      </Row>
      <Row icon={overallReport.survey_completed ? successIcon : warningIcon}>
        {
          company.lastCompletedSurveyDate
            ?
            <>
              Latest Completed report:
              <div>{`${formatDate(company.lastCompletedSurveyDate, DATE.MONTH_YEAR)} ${completedMessage}`}</div>
            </>
            :
            'No reports have been Completed'
        }
      </Row>
    </div>
  )

  return <SimpleTooltip text={tooltip}>
    <div className='company-status-icon-container info-pointer'>
      <i className={`fa ${icon}`} />{showLabel && labelText}
    </div>
  </SimpleTooltip>;
}

export default CompanyStatusIcon;
